import { React, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./ask-teacher-style.css";
import RatingDialog from "../../dialog/rating-feedback/rating-dialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import Loading from "../../dialog/loading/loading";
import ConsultService from "../../../service/consult-service";
import SiteFooter from "../../custom/site-footer/site-footer";
import TeacherInfo from "../../custom/teacher-info/teacher-info";
import PageSeo from "../../custom/page-seo/page-seo";
import useIsMobile from "../../../hooks/useIsMobile";

//問問老師：跟「專業諮詢」共用同一套諮詢邏輯，差別在標題、且頂部不顯示產品資訊卡
const AskTeacherComponent = ({ setShowServiceMenu, setShowPersonalMenu }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  //問問老師（隨意問任何問題）固定用 orderID "0"，與各產品(真實訂單ID)各自獨立一條諮詢串
  const orderID = "0";

  const [consult, setConsult] = useState(null);
  const [followupCost, setFollowupCost] = useState(0);
  const [userAPoint, setUserAPoint] = useState(0);
  //此諮詢入口是否享首次免費（個人命盤無，其他商品有）
  const [hasFreeFirst, setHasFreeFirst] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [question, setQuestion] = useState("");
  const [replyTo, setReplyTo] = useState(""); //撰寫中引用的訊息內容
  const [sending, setSending] = useState(false);

  const [showRating, setShowRating] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null); //被評分的訊息
  const [showConfirm, setShowConfirm] = useState(false);
  const [showNoPoint, setShowNoPoint] = useState(false); //A-Point不足提示

  //載入諮詢串
  const loadThread = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await ConsultService.getThread(orderID);
      const d = res.data.data;
      setConsult(d.consult);
      setFollowupCost(d.cost);
      setUserAPoint(d.balanceAfter);
      setHasFreeFirst(d.hasFreeFirst);
    } catch (e) {
      setError(e?.response?.data?.message || "讀取諮詢失敗");
    } finally {
      setLoading(false);
    }
  }, [orderID]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  const messages = consult?.messages || [];
  //享首次免費 且 尚無任何使用者訊息 → 這次提問免費
  const canFree =
    hasFreeFirst && messages.filter((m) => m.sender === "user").length === 0;

  //聊天區捲到底
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages.length]);

  //點「回覆」→ 帶入引用 + 捲到輸入框
  const handleReply = (content) => {
    setReplyTo(content);
    requestAnimationFrame(() => {
      inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  //送出諮詢
  const submitQuestion = async () => {
    setShowConfirm(false);
    if (!question.trim() || sending) return;
    setSending(true);
    try {
      const res = await ConsultService.sendMessage({
        consultID: consult.consultID,
        content: question.trim(),
        replyTo,
      });
      setConsult(res.data.data.consult);
      setUserAPoint(res.data.data.balanceAfter);
      setQuestion("");
      setReplyTo("");
    } catch (e) {
      alert(e?.response?.data?.message || "送出失敗");
    } finally {
      setSending(false);
    }
  };

  //點「送出諮詢」→ 付費追問先檢查 A-Point是否足夠，再二次確認
  const handleSubmitClick = () => {
    if (!question.trim() || sending) return;
    //付費追問且 A-Point不足 → 跳提示，導去點數管理
    if (!canFree && userAPoint < followupCost) {
      setShowNoPoint(true);
      return;
    }
    setShowConfirm(true);
  };

  //開啟評分
  const openRating = (message) => {
    setRatingTarget(message);
    setShowRating(true);
  };

  //評分送出（含文字回饋）
  const handleRate = async (stars, feedback) => {
    if (!ratingTarget) return;
    try {
      const res = await ConsultService.rate({
        consultID: consult.consultID,
        messageID: ratingTarget.messageID,
        stars,
        feedback: feedback || "",
      });
      setConsult(res.data.data.consult);
    } catch (e) {
      alert(e?.response?.data?.message || "評分失敗");
    }
  };

  //聊天訊息區
  const renderChat = () => {
    if (messages.length === 0) return null;
    return (
      <div className="consult-chat" ref={chatRef}>
        {messages.map((message) => (
          <div className="message" key={message.messageID}>
            {message.sender === "user" ? (
              <div className="customer">
                <div
                  className="action-c"
                  onClick={() => handleReply(message.content)}
                >
                  <img src="../images/ic-reply.png" alt="" />
                  <span>回覆</span>
                </div>
                <div className="msg-c">
                  {message.replyTo && (
                    <div className="reply-area">
                      <span className="reply">{`回覆：${message.replyTo}`}</span>
                      <div className="msg-line"></div>
                    </div>
                  )}
                  <span>{message.content}</span>
                </div>
              </div>
            ) : (
              <div className="teacher">
                <div className="msg-area">
                  <div className="msg-t">
                    {message.replyTo && (
                      <div className="reply-area">
                        <span className="reply">{`回覆：${message.replyTo}`}</span>
                        <div className="msg-line"></div>
                      </div>
                    )}
                    <span>{message.content}</span>
                  </div>

                  {Number(message.stars) > 0 && (
                    <div className="stars">
                      {Array.from({ length: message.stars }).map((_, i) => (
                        <img
                          key={`f${i}`}
                          src="../images/ic-gold-fill-star.png"
                          alt=""
                        />
                      ))}
                      {Array.from({ length: 5 - message.stars }).map((_, i) => (
                        <img
                          key={`e${i}`}
                          src="../images/ic-gold-empty-star.png"
                          alt=""
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div
                  className={`action-t ${
                    message.stars > 0 ? "action-t-recoup" : ""
                  }`}
                  onClick={() => handleReply(message.content)}
                >
                  <img src="../images/ic-reply.png" alt="" />
                  <span>回覆</span>
                </div>

                {Number(message.stars) === 0 && (
                  <div className="action-t" onClick={() => openRating(message)}>
                    <img src="../images/ic-default-star.png" alt="" />
                    <span>評分</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  //輸入區 + 送出按鈕
  const renderSubmit = () => (
    <>
      <div className="submit-card" ref={inputRef}>
        {replyTo && (
          <>
            <div className="reply-msg">
              <span>{`回覆：${replyTo}`}</span>
              <img
                src="../images/ic-default-close.png"
                alt=""
                onClick={() => setReplyTo("")}
              />
            </div>
            <div className="submit-line"></div>
          </>
        )}
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="歡迎提出任何命理相關的問題。建議一次聚焦一個問題，老師能為您解析得更深入完整。
老師回覆後，我們會寄信件至您的電子信箱通知您。"
        ></textarea>
      </div>

      <span
        className={`submit ${question.trim().length > 0 ? "active-submit" : ""}`}
        onClick={handleSubmitClick}
      >
        {`送出諮詢${canFree ? "（免費乙次）" : `（${followupCost} A-Point）`}`}
      </span>
    </>
  );

  //聊天主體（web / mobile 共用）：問問老師不顯示產品資訊卡
  const renderContent = () => {
    if (loading) {
      return <Loading />;
    }
    if (error) {
      return <div className="consult-title">{error}</div>;
    }
    return (
      <>
        <div className="consult-title">- 諮詢內容 -</div>
        {renderChat()}
        {renderSubmit()}
      </>
    );
  };

  return (
    <div className="ask-teacher-page">
      <PageSeo
        title="問問老師｜命理諮詢"
        description="一對一線上命理諮詢，由資深命理師親自回覆。第一次詢問免費，幫你看清命盤、流年、感情、事業上的問題。"
        path="/ask-teacher"
      />
      {/* 電腦版佈局 */}
      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
        >
          <div className="content">
            <div className="title">
              <img src="../images/ic-consult.png" alt="" />
              <span>問問老師</span>
            </div>
            <TeacherInfo />
            {renderContent()}
          </div>

          <div className="divide"></div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />
        </div>
      )}

      {/* 移動裝置佈局 */}
      {isMobile && (
        <div className="mobile-main">
          <div className="content">
            <div className="title">
              <img src="../images/ic-consult.png" alt="" />
              <span>問問老師</span>
            </div>
            <TeacherInfo />
            {renderContent()}
          </div>

          <div className="divide"></div>

          <SiteFooter />
        </div>
      )}

      {/* 評分 dialog */}
      {showRating && (
        <RatingDialog
          open={showRating}
          onClose={() => setShowRating(false)}
          onSuccess={(stars, feedback) => handleRate(stars, feedback)}
        />
      )}

      {/* 送出確認 dialog */}
      {showConfirm && (
        <ConfirmDialog
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          onSuccess={submitQuestion}
          title="確定送出諮詢？"
          message={
            (canFree
              ? "此次為免費的諮詢機會"
              : `此次諮詢將扣除 ${followupCost} A-Point（目前 ${userAPoint} A-Point）`) +
            "<br/>請確認諮詢內容無誤再行送出"
          }
          confirmTxt="確認送出"
          image="/images/ic-send.png"
        />
      )}

      {/* A-Point不足 dialog → 導向點數管理 */}
      {showNoPoint && (
        <ConfirmDialog
          open={showNoPoint}
          onClose={() => setShowNoPoint(false)}
          onSuccess={() => navigate("/point")}
          title="A-Point 點數不足"
          message={
            `此次諮詢需 ${followupCost} A-Point，您目前只有 ${userAPoint} A-Point` +
            "<br/>請前往點數管理進行儲值"
          }
          confirmTxt="立即儲值"
          image="/images/ic-default-lock.png"
        />
      )}
    </div>
  );
};

export default AskTeacherComponent;
